import React from "react";
import { useTranslation } from "react-i18next";
import {
  baselineGroupBackgroundColor,
  baselineGroupBorder,
  baselineGroupBorderRadius,
  baselineGroupPadding,
} from "../domain/baselines";
import { findGroup } from "../domain/find";
import { translateGroupLabel } from "../domain/labels";
import { selectDocument } from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";
import { SettingSection } from "./controls/SettingSection";
import { Panel } from "./Panel";
import {
  SurfaceAppearanceSection,
  SurfaceCornerRoundingFields,
} from "./SurfaceAppearanceFields";
import { EmailBuilderSettingsPaddingSection } from "./controls/EmailBuilderSettingsPaddingSection";
import { GROUP_SETTINGS_DEFAULT_EXPANDED } from "./settingsPanelDefaults";
import {
  isCornerRoundingInactive,
  isPaddingInactive,
  surfaceCornerRoundingAddDefault,
  surfacePaddingAddDefault,
} from "./settingsSectionActivation";

type GroupSettingsPanelProps = {
  groupId: string;
};

export function GroupSettingsPanel({ groupId }: GroupSettingsPanelProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const { updateGroup } = useEditorActions();
  const group = findGroup(document.sections, groupId);
  if (!group) {
    return null;
  }

  const defaultPadding = baselineGroupPadding();
  const defaultBorder = baselineGroupBorder();
  const defaultRadius = baselineGroupBorderRadius();
  const appearanceBaselines = {
    backgroundColor: baselineGroupBackgroundColor(),
    border: defaultBorder,
    borderRadius: defaultRadius,
  };

  return (
    <Panel
      title={translateGroupLabel(t)}
      defaultExpandedSectionIds={GROUP_SETTINGS_DEFAULT_EXPANDED}
    >
      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(group.padding),
          onAdd: () => updateGroup(group.id, { padding: surfacePaddingAddDefault() }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Group padding (px)")}
          value={group.padding}
          defaultValue={defaultPadding}
          onChange={padding => {
            updateGroup(group.id, { padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="corner-rounding"
        title={t("Corner rounding")}
        activation={{
          mode: "optional",
          isActive: !isCornerRoundingInactive(group.borderRadius),
          onAdd: () =>
            updateGroup(group.id, { borderRadius: surfaceCornerRoundingAddDefault() }),
        }}
      >
        <SurfaceCornerRoundingFields
          value={group}
          baselines={appearanceBaselines}
          onChange={patch => updateGroup(group.id, patch)}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <SurfaceAppearanceSection
          value={group}
          baselines={appearanceBaselines}
          allowTransparentBackground
          showBackgroundImage
          onChange={patch => updateGroup(group.id, patch)}
        />
      </SettingSection>
    </Panel>
  );
}
